import { useEffect } from "react";

export interface HreflangAlternate {
  // BCP-47 hreflang code (e.g. "en", "es", "fr-CA", "pa", "x-default").
  hreflang: string;
  // Site-relative or absolute href; absolute is recommended by Google.
  href: string;
}

export interface DocumentMeta {
  title: string;
  description: string;
  canonicalPath: string;
  robots?: string;
  jsonLd?: Array<Record<string, unknown>>;
  // hreflang alternates to emit as <link rel="alternate" hreflang="…">
  // tags. The current page's own variant should be included so search
  // engines see a complete set on every locale variant.
  alternates?: HreflangAlternate[];
}

// Production canonical is https://jinglemission.com (see
// vite.config.ts → getSiteUrl and public/sitemap.xml). The
// VITE_PUBLIC_SITE_URL secret can override for staging/preview.
const SITE_URL: string =
  (import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined)?.replace(
    /\/+$/,
    "",
  ) ?? "https://jinglemission.com";

function setMeta(selector: string, attr: "name" | "property", value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  const previous = el?.getAttribute("content") ?? null;
  if (!el) {
    el = document.createElement("meta");
    const [, key] = selector.match(/\[(?:name|property)="([^"]+)"\]/) ?? [];
    if (key) el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
  return previous;
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  const previous = el?.getAttribute("href") ?? null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return previous;
}

export function useDocumentMeta(meta: DocumentMeta): void {
  const jsonLdSerialized = meta.jsonLd ? JSON.stringify(meta.jsonLd) : "";

  useEffect(() => {
    const previousTitle = document.title;
    const canonicalUrl = `${SITE_URL}${meta.canonicalPath}`;

    document.title = meta.title;
    const previousDescription = setMeta(
      'meta[name="description"]',
      "name",
      meta.description,
    );
    const robotsValue = meta.robots ?? "index, follow";
    const previousRobots = setMeta(
      'meta[name="robots"]',
      "name",
      robotsValue,
    );
    const previousGooglebot = setMeta(
      'meta[name="googlebot"]',
      "name",
      robotsValue === "index, follow" ? "index, follow, max-image-preview:large" : robotsValue,
    );
    const previousCanonical = setLink("canonical", canonicalUrl);
    const previousOgUrl = setMeta(
      'meta[property="og:url"]',
      "property",
      canonicalUrl,
    );
    const previousOgTitle = setMeta(
      'meta[property="og:title"]',
      "property",
      meta.title,
    );
    const previousOgDescription = setMeta(
      'meta[property="og:description"]',
      "property",
      meta.description,
    );
    const previousTwitterTitle = setMeta(
      'meta[name="twitter:title"]',
      "name",
      meta.title,
    );
    const previousTwitterDescription = setMeta(
      'meta[name="twitter:description"]',
      "name",
      meta.description,
    );

    const injectedScripts: HTMLScriptElement[] = [];
    if (meta.jsonLd && meta.jsonLd.length > 0) {
      for (const block of meta.jsonLd) {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-route-jsonld", "");
        script.text = JSON.stringify(block);
        document.head.appendChild(script);
        injectedScripts.push(script);
      }
    }

    // hreflang alternates. Always wipe any previously-injected set first
    // so a page that omits alternates doesn't inherit the previous page's
    // tags during SPA navigation. data-route-hreflang lets us scope the
    // teardown to ones we own (the index.html shell never injects any).
    const previousAlternates = Array.from(
      document.head.querySelectorAll<HTMLLinkElement>(
        'link[data-route-hreflang]',
      ),
    );
    for (const el of previousAlternates) el.remove();
    const injectedAlternates: HTMLLinkElement[] = [];
    if (meta.alternates && meta.alternates.length > 0) {
      for (const alt of meta.alternates) {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", alt.hreflang);
        link.setAttribute("href", alt.href);
        link.setAttribute("data-route-hreflang", "");
        document.head.appendChild(link);
        injectedAlternates.push(link);
      }
    }

    return () => {
      document.title = previousTitle;
      if (previousDescription !== null)
        setMeta('meta[name="description"]', "name", previousDescription);
      if (previousRobots !== null)
        setMeta('meta[name="robots"]', "name", previousRobots);
      if (previousGooglebot !== null)
        setMeta('meta[name="googlebot"]', "name", previousGooglebot);
      if (previousCanonical !== null) setLink("canonical", previousCanonical);
      if (previousOgUrl !== null)
        setMeta('meta[property="og:url"]', "property", previousOgUrl);
      if (previousOgTitle !== null)
        setMeta('meta[property="og:title"]', "property", previousOgTitle);
      if (previousOgDescription !== null)
        setMeta(
          'meta[property="og:description"]',
          "property",
          previousOgDescription,
        );
      if (previousTwitterTitle !== null)
        setMeta('meta[name="twitter:title"]', "name", previousTwitterTitle);
      if (previousTwitterDescription !== null)
        setMeta(
          'meta[name="twitter:description"]',
          "name",
          previousTwitterDescription,
        );
      for (const s of injectedScripts) {
        s.remove();
      }
      for (const l of injectedAlternates) {
        l.remove();
      }
    };
  }, [
    meta.title,
    meta.description,
    meta.canonicalPath,
    meta.robots,
    jsonLdSerialized,
    // Stringify alternates so the effect re-runs when the set changes.
    JSON.stringify(meta.alternates ?? []),
  ]);
}

export function getSiteUrl(): string {
  return SITE_URL;
}

// Hreflang alternates for in-app marketing/legal pages whose translation
// happens in-place via the i18next picker (same canonical URL serves all
// four locales). Emits one self-referential alternate per supported
// locale plus an x-default that points at the same canonical, so search
// engines can see that the page is available in en / es / fr-CA / pa
// even though there is no per-locale URL path.
//
// Use this on routes like /pricing, /faq, /help, /about, /contact —
// public, indexable pages that do NOT have distinct localized URLs the
// way SEO topic pages do (those build their own alternates in
// SeoTopicPage.tsx using SEO_PAGE_PATHS).
export function inPlaceLocaleAlternates(canonicalPath: string): HreflangAlternate[] {
  const href = `${SITE_URL}${canonicalPath}`;
  return [
    { hreflang: "en", href },
    { hreflang: "es", href },
    { hreflang: "fr-CA", href },
    { hreflang: "pa", href },
    { hreflang: "x-default", href },
  ];
}
